import ApplicationController, { ApplicationView } from "@mixitone/mvc";
import dayjs from "dayjs";

const DEFAULT_DATE_FORMAT = "D MMM YYYY";

interface Props {
  date: string | Date;
  className?: string;
  granularity?: "day" | "hour" | "minute";
  /** If set, dates older than this many days are shown as formatted dates instead of relative time. */
  maxRelativeDays?: number;
  /** dayjs format string used when date is older than maxRelativeDays. Default "D MMM YYYY". */
  dateFormat?: string;
}

interface State {
  date: string | Date;
  granularity?: "day" | "hour" | "minute";
  maxRelativeDays?: number;
  dateFormat: string;
  relativeTime: string;
}

class FromNowController extends ApplicationController<State, Props> {
  timer: ReturnType<typeof setInterval> | null = null;

  async changeProps(newProps: Props) {
    this.state.date = newProps.date;
    this.state.granularity = newProps.granularity;
    this.state.maxRelativeDays = newProps.maxRelativeDays;
    this.state.dateFormat = newProps.dateFormat ?? DEFAULT_DATE_FORMAT;
    this.updateRelativeTime();
    this.timer = setInterval(() => this.updateRelativeTime(), 60000);
  }

  updateRelativeTime() {
    const dateObj = dayjs(this.state.date);
    const now = dayjs();
    const maxDays = this.state.maxRelativeDays;

    if (maxDays != null && dateObj.isBefore(now.subtract(maxDays, "day"), "day")) {
      this.state.relativeTime = dateObj.format(this.state.dateFormat);
      return;
    }

    if (this.state.granularity === "day" && dateObj.isSame(dayjs(), "day")) {
      this.state.relativeTime = "today";
    } else if (dateObj.isSame(dayjs().subtract(1, "day"), "day")) {
      this.state.relativeTime = "yesterday";
    } else {
      this.state.relativeTime = dateObj.fromNow();
    }
  }

  destroy(): void {
    if (this.timer) {
      clearInterval(this.timer);
    }
  }
}

const FromNow: React.FC<Props> = ({ className }) => {
  const controller = FromNowController.use();
  const { relativeTime } = controller.state;

  return <span className={className}>{relativeTime}</span>;
};

/**
 * Show a relative time from now that updates every minute.
 * Use maxRelativeDays + dateFormat to show an absolute date when older than N days.
 *
 * @usage
 * ```tsx
 * <FromNow date={user.signedUpAt} />
 * <FromNow date={user.signedUpAt} granularity="day" />
 * <FromNow date={item.occurredAt} maxRelativeDays={7} dateFormat="D MMM YYYY" />
 * ```
 */
const ScopedFromNow = FromNowController.scope(ApplicationView(FromNow));
export { ScopedFromNow as FromNow };
